(() => {
  class PastaelColorMediaGallery {
    constructor(root, options = {}) {
      this.root = root;
      this.stage = options.stage || root.querySelector('[data-card-media-stage]');
      this.track = this.stage?.querySelector('[data-card-media-track]');
      this.stash = this.stage?.querySelector('[data-card-media-stash]');
      this.slides = Array.from(this.track?.querySelectorAll('[data-media-id]') || []);
      this.prevButton = options.prevButton || root.querySelector('[data-card-media-prev]');
      this.nextButton = options.nextButton || root.querySelector('[data-card-media-next]');
      this.colorValuesScript = options.colorValuesScript || root.querySelector('[data-card-gallery-colors]');
      this.activeIndex = 0;
      this.transitionMs = 450;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.onNativeDragStart = (event) => event.preventDefault();
      this.sliderMedia = window.matchMedia('(min-width: 750px)');
      this.onSliderMediaChange = () => this.syncSliderInteractionState();
    }

    isSliderEnabled() {
      return this.sliderMedia?.matches ?? window.matchMedia('(min-width: 750px)').matches;
    }

    syncSliderInteractionState() {
      const visibleCount = this.visibleSlides.length;
      if (this.dragState) this.resetDrag(this.dragState.pointerId);
      this.stage?.classList.toggle('is-draggable', visibleCount > 1 && this.isSliderEnabled());
    }

    init(initialColor, preferredMediaId) {
      if (!this.stage || !this.track || !this.slides.length) return;

      this.assignMediaColors();
      this.sliderMedia?.addEventListener('change', this.onSliderMediaChange);
      this.stage.addEventListener('pointerdown', this.onPointerDown);
      this.stage.addEventListener('pointermove', this.onPointerMove, { passive: false });
      this.stage.addEventListener('pointerup', this.onPointerUp);
      this.stage.addEventListener('pointercancel', this.onPointerCancel);
      this.stage.addEventListener('dragstart', this.onNativeDragStart);

      this.prevButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.step(-1);
      });
      this.nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.step(1);
      });

      this.filterByColor(initialColor || '', preferredMediaId, false);
    }

    destroy() {
      this.sliderMedia?.removeEventListener('change', this.onSliderMediaChange);
      this.stage?.removeEventListener('pointerdown', this.onPointerDown);
      this.stage?.removeEventListener('pointermove', this.onPointerMove);
      this.stage?.removeEventListener('pointerup', this.onPointerUp);
      this.stage?.removeEventListener('pointercancel', this.onPointerCancel);
      this.stage?.removeEventListener('dragstart', this.onNativeDragStart);
      if (this.dragState) this.resetDrag(this.dragState.pointerId);
    }

    get visibleSlides() {
      return this.slides.filter((slide) => !slide.hasAttribute('data-filter-hidden'));
    }

    get hadRecentSwipe() {
      return Date.now() - (this.lastSwipeAt || 0) < 400;
    }

    step(direction) {
      const visibleSlides = this.visibleSlides;
      if (visibleSlides.length < 2) return;

      const activeIndex = Math.max(
        0,
        visibleSlides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      const nextIndex = (activeIndex + direction + visibleSlides.length) % visibleSlides.length;
      this.setActiveIndex(nextIndex, true);
    }

    onPointerDown(event) {
      if (
        !this.stage?.classList.contains('is-draggable') ||
        event.button !== 0 ||
        event.target.closest('button, a, input, select, textarea')
      ) {
        return;
      }

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
      };
      this.track.classList.add('is-dragging');
      this.stage.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const deltaX = event.clientX - this.dragState.startX;
      const deltaY = event.clientY - this.dragState.startY;
      if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

      if (event.cancelable) event.preventDefault();
      const maxOffset = this.stage.clientWidth * 0.35;
      const offset = Math.sign(deltaX) * Math.min(Math.abs(deltaX), maxOffset);
      this.stage.style.setProperty('--pastael-card-gallery-drag-offset', `${offset}px`);
    }

    onPointerUp(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const deltaX = event.clientX - this.dragState.startX;
      const deltaY = event.clientY - this.dragState.startY;
      const threshold = Math.max(32, this.stage.clientWidth * 0.08);
      const shouldStep = Math.abs(deltaX) >= threshold && Math.abs(deltaX) > Math.abs(deltaY);
      this.resetDrag(event.pointerId);

      if (shouldStep) {
        this.lastSwipeAt = Date.now();
        this.step(deltaX < 0 ? 1 : -1);
      }
    }

    onPointerCancel(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
      this.resetDrag(event.pointerId);
    }

    resetDrag(pointerId) {
      if (this.stage?.hasPointerCapture?.(pointerId)) {
        this.stage.releasePointerCapture(pointerId);
      }
      this.dragState = null;
      this.track?.classList.remove('is-dragging');
      this.stage?.style.removeProperty('--pastael-card-gallery-drag-offset');
    }

    syncTrackLayout() {
      this.slides.forEach((slide) => {
        const destination = slide.hasAttribute('data-filter-hidden') ? this.stash : this.track;
        destination?.appendChild(slide);
      });
    }

    setActiveIndex(index, animate = true) {
      const visibleSlides = this.visibleSlides;
      if (!visibleSlides.length) return;

      const nextIndex = ((index % visibleSlides.length) + visibleSlides.length) % visibleSlides.length;
      this.activeIndex = nextIndex;

      visibleSlides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === nextIndex);
      });

      this.applyTrackTransform(animate);
    }

    setActiveMedia(mediaId, animate = true) {
      const visibleSlides = this.visibleSlides;
      const index = visibleSlides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      this.setActiveIndex(index >= 0 ? index : 0, animate);
    }

    applyTrackTransform(animate) {
      if (!this.track) return;

      this.track.classList.toggle('is-animate', animate);
      this.stage.style.setProperty('--pastael-card-gallery-index', String(this.activeIndex));
    }

    normalizeMediaSource(value) {
      let filename = String(value || '');
      try {
        filename = new URL(filename, window.location.origin).pathname.split('/').pop() || '';
        filename = decodeURIComponent(filename);
      } catch (error) {
        filename = filename.split('/').pop()?.split('?')[0] || '';
      }

      return filename
        .toLowerCase()
        .replace(/\.[^.]+$/, '')
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '')
        .replace(/[^a-z]+/g, '');
    }

    mediaTrigrams(value) {
      const normalized = this.normalizeMediaSource(value);
      if (!normalized) return new Set();
      if (normalized.length < 3) return new Set([normalized]);

      const trigrams = new Set();
      for (let index = 0; index <= normalized.length - 3; index += 1) {
        trigrams.add(normalized.slice(index, index + 3));
      }
      return trigrams;
    }

    getGalleryColors() {
      try {
        return JSON.parse(this.colorValuesScript?.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    buildColorSignatures(colors, getTrigrams) {
      const signatures = colors
        .map((entry) => ({
          color: typeof entry === 'string' ? entry : entry.color,
          trigrams: getTrigrams(entry),
        }))
        .filter((signature) => signature.trigrams.size > 0);
      const trigramCounts = new Map();

      signatures.forEach((signature) => {
        signature.trigrams.forEach((trigram) => {
          trigramCounts.set(trigram, (trigramCounts.get(trigram) || 0) + 1);
        });
      });

      signatures.forEach((signature) => {
        signature.uniqueTrigrams = new Set(
          Array.from(signature.trigrams).filter((trigram) => trigramCounts.get(trigram) === 1)
        );
      });

      return signatures;
    }

    matchMediaColor(mediaSource, signatures) {
      const mediaTrigrams = this.mediaTrigrams(mediaSource);
      const rankedSignatures = signatures
        .map((signature) => {
          let score = 0;
          signature.uniqueTrigrams.forEach((trigram) => {
            if (mediaTrigrams.has(trigram)) score += 1;
          });
          return { color: signature.color, score };
        })
        .sort((left, right) => right.score - left.score);
      const bestMatch = rankedSignatures[0];
      const secondBestScore = rankedSignatures[1]?.score || 0;

      return bestMatch?.score > secondBestScore ? bestMatch.color : '';
    }

    assignMediaColors() {
      const anchorsByColor = new Map();
      const galleryColors = this.getGalleryColors();
      const colorSignatures = this.buildColorSignatures(galleryColors, (color) => this.mediaTrigrams(color));

      this.slides.forEach((slide) => {
        const colorKey = this.normalizeColor(slide.dataset.variantColor);
        if (!colorKey || !slide.dataset.mediaSource || anchorsByColor.has(colorKey)) return;
        anchorsByColor.set(colorKey, {
          color: slide.dataset.variantColor,
          trigrams: this.mediaTrigrams(slide.dataset.mediaSource),
        });
      });

      const anchors = Array.from(anchorsByColor.values());
      const anchorSignatures =
        anchors.length > 1 ? this.buildColorSignatures(anchors, (anchor) => anchor.trigrams) : [];

      this.slides.forEach((slide) => {
        const explicitColor = this.matchMediaColor(slide.dataset.mediaSource, colorSignatures);
        const inferredColor =
          explicitColor || this.matchMediaColor(slide.dataset.mediaSource, anchorSignatures);
        if (inferredColor) slide.dataset.mediaColor = inferredColor;
      });
    }

    normalizeColor(value) {
      return String(value || '')
        .trim()
        .toLowerCase();
    }

    findMediaForColor(color) {
      const colorKey = this.normalizeColor(color);
      if (!colorKey) return null;

      return (
        this.slides.find((slide) => this.normalizeColor(slide.dataset.mediaColor) === colorKey)?.dataset
          .mediaId || null
      );
    }

    filterByColor(color, preferredMediaId, animate = false) {
      const selectedColor = this.normalizeColor(color);

      this.slides.forEach((slide) => {
        const mediaColor = this.normalizeColor(slide.dataset.mediaColor);
        const isFiltered = Boolean(selectedColor && mediaColor && mediaColor !== selectedColor);
        slide.toggleAttribute('data-filter-hidden', isFiltered);
        slide.classList.remove('is-active');
      });

      this.syncTrackLayout();

      const visibleCount = this.visibleSlides.length;
      if (this.dragState) this.resetDrag(this.dragState.pointerId);

      const card = this.root.querySelector('.pastael-card-product') || this.root;

      this.root.classList.toggle('has-card-gallery', this.slides.length > 1);
      this.root.classList.toggle('has-card-gallery-multiple', visibleCount > 1);
      card.classList.toggle('has-card-gallery', this.slides.length > 1);
      card.classList.toggle('has-card-gallery-multiple', visibleCount > 1);
      this.syncSliderInteractionState();
      this.prevButton?.toggleAttribute('hidden', visibleCount < 2 || !this.isSliderEnabled());
      this.nextButton?.toggleAttribute('hidden', visibleCount < 2 || !this.isSliderEnabled());

      const mediaId = preferredMediaId || this.findMediaForColor(color) || this.visibleSlides[0]?.dataset.mediaId;
      if (mediaId) {
        this.setActiveMedia(mediaId, animate);
      } else {
        this.setActiveIndex(0, animate);
      }
    }
  }

  window.PastaelColorMediaGallery = PastaelColorMediaGallery;
})();
